import {
  CogIcon,
  DropletsIcon,
  FactoryIcon,
  FilterIcon,
  FlaskConicalIcon,
  GraduationCapIcon,
  MagnetIcon,
  PickaxeIcon,
  ShieldCheckIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  context: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Mineral Processing Fundamentals: The Foundation',
    subtitle: 'The foundation your students need to understand everything else',
    context:
      'Your first introduction to mineral processing can be transformative or overwhelming. This volume transforms technical concepts into something comprehensible and relevant from day one.',
    items: [
      'Introduction to Mineral Processing and Its Industrial Importance',
      'Basic Mineral Properties and Ore Characteristics',
      'Essential Mineral Processing Terminology',
      'Overview of Mineral Processing Stages and Workflows',
      'Principles of Mineral Liberation and Concentration',
      'Introduction to Recovery, Grade, and Mass Balance Concepts',
      'Process Flow and Circuit Integration',
      'Mineral Processing in Mining Operations',
      'Industrial Equipment Overview',
      'Economic and Environmental Considerations',
      'Safety and Operational Awareness',
      'Engineering Roles and Responsibilities',
      'Connections Between Mining, Processing, and Recovery',
      'Classroom Applications and Conceptual Examples',
      'Review & Teacher Guidance',
    ],
    result:
      'Your students understand mineral processing as a practical discipline that connects mineralogy, equipment operation, and resource recovery — not an impossible list of terms to memorize.',
    icon: PickaxeIcon,
  },
  {
    number: '2',
    title: 'Crushing, Grinding & Comminution',
    subtitle: 'Everything your students need to know about particle size reduction',
    context:
      'This is where many students struggle to build the comminution foundation. This volume gives you the materials to help them truly understand size reduction.',
    items: [
      'Fundamentals of Particle Size Reduction',
      'Primary, Secondary, and Tertiary Crushing Processes',
      'Jaw Crushers and Operating Principles',
      'Cone Crushers and Impact Crushers',
      'Crusher Selection and Capacity',
      'Ball Mills and Rod Mills',
      'Grinding Equipment and Performance',
      'Reduction Ratios and Energy Consumption',
      'Comminution Circuit Design',
      'Grinding Efficiency and Optimization',
      'Ore Hardness and Grindability',
      'Size Distribution and Liberation',
      'Equipment Wear and Maintenance',
      'Practical Examples of Comminution Circuits',
      'Industrial Applications',
      'Troubleshooting Reasoning',
      'Classroom Applications',
      '15 worked crushing, grinding, and comminution examples',
    ],
    result: 'Your students learn the comminution principles that underpin all mineral processing circuits.',
    icon: CogIcon,
  },
  {
    number: '3',
    title: 'Screening, Classification & Particle Size Separation',
    subtitle: 'Explore how particles are separated by size for processing efficiency',
    context:
      'Classification is critical but complex. This volume gives you everything needed so your students grasp separation clearly.',
    items: [
      'Principles of Particle Size Classification',
      'Industrial Screening Methods and Equipment',
      'Vibrating Screens and Screen Selection',
      'Screening Efficiency and Performance',
      'Hydrocyclone Fundamentals',
      'Hydrocyclone Operation and Classification',
      'Mechanical Classifiers',
      'Spiral Classifiers',
      'Particle Size Distribution',
      'Separation Performance and Metrics',
      'Classification Circuit Integration',
      'Equipment Maintenance Concepts',
      'Common Classification Problems',
      'Troubleshooting Reasoning',
      'Classroom Applications and Integrated Cases',
      '15 worked screening and classification examples',
    ],
    result:
      'Your students understand how classification systems separate particles and how to evaluate separation performance.',
    icon: FilterIcon,
  },
  {
    number: '4',
    title: 'Gravity Separation & Magnetic Separation',
    subtitle: 'Explore density and magnetic-based mineral concentration methods',
    context: 'Separation methods are diverse and important. This volume makes these systems understandable.',
    items: [
      'Fundamentals of Gravity-Based Mineral Separation',
      'Density Differences and Mineral Concentration Principles',
      'Heavy Media Separation Concepts',
      'Jigging and Jig Operations',
      'Shaking Tables and Spiral Concentrators',
      'Density Separation Performance',
      'Magnetic Properties of Minerals',
      'Low-Intensity Magnetic Separation',
      'High-Intensity Magnetic Separation',
      'Magnetic Separation Equipment',
      'Separation Efficiency and Recovery',
      'Industrial Applications of Magnetic Separation',
      'Comparisons of Separation Methods',
      'Equipment Selection and Circuit Design',
      'Troubleshooting and Optimization',
      'Classroom Applications',
      '12 worked gravity and magnetic separation examples',
    ],
    result:
      'Your students can explain gravity and magnetic separation methods and understand when to apply each technique.',
    icon: MagnetIcon,
  },
  {
    number: '5',
    title: 'Froth Flotation & Mineral Concentration',
    subtitle: 'Understand the most widely used mineral concentration process',
    context: 'Flotation is complex chemistry and engineering. This volume makes it accessible and understandable.',
    items: [
      'Fundamentals of Froth Flotation',
      'Surface Chemistry and Mineral Hydrophobicity',
      'Collectors, Frothers, and Modifiers',
      'Flotation Reagents and Their Functions',
      'Flotation Cells and Equipment',
      'Industrial Circuit Configurations',
      'Rougher, Cleaner, and Scavenger Flotation Stages',
      'Concentrate Grade and Mineral Recovery',
      'Process Performance and Metrics',
      'Flotation Circuit Design',
      'Reagent Selection and Optimization',
      'Water Quality Effects',
      'Environmental Considerations',
      'Troubleshooting and Process Control',
      'Classroom Applications',
      'Integrated Flotation Cases',
      '12 worked flotation and mineral concentration examples',
    ],
    result:
      'Your students understand how flotation works as a chemical and engineering process and can analyze flotation performance.',
    icon: FlaskConicalIcon,
  },
  {
    number: '6',
    title: 'Dewatering, Thickening & Filtration',
    subtitle: 'Shows how solids are separated from liquids in mineral processing',
    context: 'Dewatering is essential to plant operations. This volume makes these concepts clear and applicable.',
    items: [
      'Fundamentals of Solid-Liquid Separation',
      'Industrial Thickening Processes',
      'Thickening Equipment and Operation',
      'Sedimentation Principles',
      'Flocculation and Settling Concepts',
      'Thickener Design and Performance',
      'Vacuum Filtration Fundamentals',
      'Pressure Filtration Methods',
      'Filter Types and Equipment',
      'Moisture Reduction and Concentrate Handling',
      'Water Recovery and Recycling',
      'Dewatering Efficiency and Metrics',
      'Environmental Water Management',
      'Troubleshooting Dewatering Problems',
      'Classroom Applications',
      '12 worked dewatering and filtration examples',
    ],
    result:
      'Your students understand how dewatering systems separate solids and liquids and contribute to plant efficiency.',
    icon: DropletsIcon,
  },
  {
    number: '7',
    title: 'Mineral Processing Plant Operations & Optimization',
    subtitle: 'Integrates all processing stages into plant operations',
    context: 'Plant operations tie everything together. This volume develops systems-level thinking.',
    items: [
      'Organization and Operation of Mineral Processing Plants',
      'Integration of Crushing, Grinding, Separation, and Dewatering Circuits',
      'Process Monitoring and Operational Performance Indicators',
      'Mass Balances and Recovery Calculations',
      'Plant Efficiency Metrics and Optimization',
      'Process Control and Automation Concepts',
      'Common Operational Problems and Troubleshooting Strategies',
      'Introduction to Process Optimization and Continuous Improvement',
      'Data Interpretation and Decision-Making',
      'Equipment Integration and Circuit Design',
      'Production Planning and Scheduling',
      'Quality Control and Concentrate Specifications',
      'Energy Consumption and Cost Optimization',
      'Operational Cases and Scenarios',
      'Classroom Operations Activities',
      '15 practical plant operations and optimization case studies',
    ],
    result:
      'Your students can analyze plant operations, identify inefficiencies, and develop improvement strategies.',
    icon: FactoryIcon,
  },
  {
    number: '8',
    title: 'Industrial Safety, Environmental Management & Sustainability',
    subtitle: 'Integrates safety and environmental responsibility into mineral processing',
    context:
      'Safety and environment are fundamental to modern mineral processing. This volume develops responsible professionals.',
    items: [
      'Industrial Safety Principles in Mineral Processing Facilities',
      'Identification and Prevention of Operational Hazards',
      'Safe Handling of Processing Equipment',
      'Chemical Reagent Safety and Management',
      'Personal Protective Equipment and Safe Work Practices',
      'Dust Control and Air Quality Management',
      'Water Management and Responsible Resource Consumption',
      'Tailings Management and Environmental Impact Reduction',
      'Waste Minimization and Recycling Concepts',
      'Sustainable Practices in Mineral Processing',
      'Environmental Responsibility and Compliance',
      'Energy Efficiency and Sustainability',
      'Community Relations and Social Responsibility',
      'Incident Learning and Continuous Improvement',
      'Classroom Safety and Environmental Scenarios',
      '15 practical safety and environmental case studies',
    ],
    result:
      'Your students understand that mineral processing safety and sustainability are built on hazard awareness, responsible practices, and continuous improvement.',
    icon: ShieldCheckIcon,
  },
  {
    number: '9',
    title: 'Teaching Mineral Processing: Methods, Activities & Educational Strategies',
    subtitle: 'Your complete guide as a mineral processing education professional',
    context:
      'This volume gives you exactly what you need to plan, organize, and evaluate your entire year of mineral processing teaching.',
    items: [
      'How to Teach Mineral Processing',
      'Organizing Technical Content for Students',
      'Teaching Through Processing Stages',
      'Teaching Technical Vocabulary',
      'Teaching Mineral Processing Fundamentals',
      'Teaching Comminution and Size Reduction',
      'Teaching Classification and Screening',
      'Teaching Separation Methods',
      'Teaching Flotation Chemistry and Engineering',
      'Teaching Plant Operations',
      'Teaching Safety and Environmental Responsibility',
      'Case-Based Learning',
      'Problem-Based Learning',
      'Technical Reasoning Activities',
      'Safety-Based Learning',
      'Classroom Discussion Strategies',
      'Assessment Strategies',
      'Asking Effective Technical Questions',
      'Connecting All 9 Volumes',
      'Integrated Teaching Approaches',
      'Teacher Planning Guidance',
      'Capstone Classroom Applications',
      '10 complete integrated mineral processing projects',
    ],
    result:
      'You\'ll never wonder "what do I teach today?" again. Everything is planned and contextualized for maximum student learning.',
    icon: GraduationCapIcon,
  },
];
